import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { designSystem, commonStyles } from '../../../styles/design-system';
import { supabase } from '../../../utils/supabaseClient';
import { 
  SaveIcon, 
  UploadIcon, 
  VerifiedIcon, 
  UserIcon, 
  ExternalLinkIcon,
  CheckIcon,
  CloseIcon
} from '../../../utils/IconWrapper';

const Container = styled(motion.div)`
  ${commonStyles.card}
  width: 800px;
  max-width: 90vw;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.xl};
  padding-bottom: ${designSystem.spacing.lg};
  border-bottom: 2px solid ${designSystem.colors.border.light};

  h2 {
    ${commonStyles.titleMedium}
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: ${designSystem.typography.fontSize.xl};
  cursor: pointer;
  color: ${designSystem.colors.text.secondary};
  transition: all ${designSystem.transitions.normal};
  
  &:hover {
    color: ${designSystem.colors.text.primary};
    transform: scale(1.1);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.xl};
  padding: ${designSystem.spacing.lg};
  background: ${designSystem.colors.background.secondary};
  border-radius: ${designSystem.borderRadius.lg};

  .avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: ${designSystem.colors.background.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: #000000;
    font-size: ${designSystem.typography.fontSize.lg};
  }

  .details {
    flex: 1;
    
    .name {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.semibold};
      margin-bottom: 4px;
    }
    
    .email {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
    }
  }

  .status {
    text-align: right;
  }
`;

const StatusBadge = styled.span<{ status: 'verified' | 'pending' | 'rejected' }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.full};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  ${props => {
    switch (props.status) {
      case 'verified':
        return `
          background: ${designSystem.colors.status.success}20;
          color: ${designSystem.colors.status.success};
        `;
      case 'rejected':
        return `
          background: ${designSystem.colors.status.error}20;
          color: ${designSystem.colors.status.error};
        `;
      default:
        return `
          background: ${designSystem.colors.status.warning}20;
          color: ${designSystem.colors.status.warning};
        `;
    }
  }}
`;

const VerificationSections = styled.div`
  display: grid;
  gap: ${designSystem.spacing.xl};
`;

const VerificationSection = styled.div`
  border: 2px solid ${designSystem.colors.border.light};
  border-radius: ${designSystem.borderRadius.lg};
  overflow: hidden;

  .section-header {
    background: ${designSystem.colors.background.secondary};
    padding: ${designSystem.spacing.lg};
    border-bottom: 1px solid ${designSystem.colors.border.light};

    h3 {
      color: ${designSystem.colors.text.primary};
      margin: 0;
      display: flex;
      align-items: center;
      gap: ${designSystem.spacing.sm};
    }

    .description {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
      margin-top: ${designSystem.spacing.sm};
    }
  }

  .section-content {
    padding: ${designSystem.spacing.lg};
  }
`;

const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.lg};
`;

const DocumentCard = styled.div`
  border: 2px dashed ${designSystem.colors.border.light};
  border-radius: ${designSystem.borderRadius.lg};
  padding: ${designSystem.spacing.lg};
  text-align: center;
  transition: all ${designSystem.transitions.normal};
  position: relative;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    border-color: ${designSystem.colors.primary.main};
    background: ${designSystem.colors.primary.main}10;
  }

  .document-icon {
    font-size: ${designSystem.typography.fontSize['2xl']};
    margin-bottom: ${designSystem.spacing.sm};
  }

  .document-name {
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.medium};
    margin-bottom: ${designSystem.spacing.xs};
  }

  .document-status {
    font-size: ${designSystem.typography.fontSize.sm};
  }

  &.uploaded {
    border-style: solid;
    border-color: ${designSystem.colors.status.success};
    background: ${designSystem.colors.status.success}10;
  }

  &.rejected {
    border-color: ${designSystem.colors.status.error};
    background: ${designSystem.colors.status.error}10;
  }
`;

const ServiceIntegration = styled.div`
  margin-bottom: ${designSystem.spacing.xl};

  .service-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: ${designSystem.spacing.lg};
  }
`;

const ServiceCard = styled.div<{ connected: boolean }>`
  ${commonStyles.card}
  text-align: center;
  position: relative;
  transition: all ${designSystem.transitions.normal};
  border: 2px solid ${props => props.connected ? designSystem.colors.status.success : designSystem.colors.border.light};

  .service-logo {
    width: 60px;
    height: 60px;
    margin: 0 auto ${designSystem.spacing.md};
    border-radius: ${designSystem.borderRadius.lg};
    background: ${designSystem.colors.background.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${designSystem.typography.fontSize['2xl']};
  }

  .service-name {
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    margin-bottom: ${designSystem.spacing.sm};
  }

  .service-description {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
    margin-bottom: ${designSystem.spacing.md};
    line-height: 1.5;
  }

  .service-status {
    margin-bottom: ${designSystem.spacing.md};
  }

  .service-actions {
    display: flex;
    gap: ${designSystem.spacing.sm};
    justify-content: center;
  }
`;

const VerificationResults = styled.div`
  margin-top: ${designSystem.spacing.lg};
  padding: ${designSystem.spacing.lg};
  background: ${designSystem.colors.background.tertiary};
  border-radius: ${designSystem.borderRadius.lg};

  .result-header {
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
    margin-bottom: ${designSystem.spacing.md};

    h4 {
      color: ${designSystem.colors.text.primary};
      margin: 0;
    }
  }

  .result-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${designSystem.spacing.sm} 0;
    border-bottom: 1px solid ${designSystem.colors.border.light};

    &:last-child {
      border-bottom: none;
    }

    .label {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
    }

    .value {
      display: flex;
      align-items: center;
      gap: ${designSystem.spacing.xs};
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${designSystem.spacing.md};
  justify-content: flex-end;
  margin-top: ${designSystem.spacing.xl};
  padding-top: ${designSystem.spacing.lg};
  border-top: 2px solid ${designSystem.colors.border.light};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background: ${designSystem.colors.status.success};
          color: #ffffff;
          border: 2px solid ${designSystem.colors.status.success};
          
          &:hover {
            background: transparent;
            color: ${designSystem.colors.status.success};
          }
        `;
      case 'danger':
        return `
          background: ${designSystem.colors.status.error};
          color: #ffffff;
          border: 2px solid ${designSystem.colors.status.error};
          
          &:hover {
            background: transparent;
            color: ${designSystem.colors.status.error};
          }
        `;
      case 'secondary':
        return commonStyles.buttonSecondary;
      default:
        return commonStyles.buttonPrimary;
    }
  }}
  
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.lg};
`;

const FileUpload = styled.label`
  ${commonStyles.buttonSecondary}
  display: inline-flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};
  cursor: pointer;
  
  input {
    display: none;
  }
`;

interface VerificationPanelProps {
  userId: string;
  onClose: () => void;
  onVerificationComplete: () => void;
}

interface UserData {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  email_verified: boolean;
  phone_verified: boolean;
  identity_verified: boolean;
}

interface VerificationService {
  id: string;
  name: string;
  description: string;
  logo: string;
  connected: boolean;
  lastCheck?: string;
  status: 'active' | 'pending' | 'error';
}

const VerificationPanel: React.FC<VerificationPanelProps> = ({ 
  userId, 
  onClose, 
  onVerificationComplete 
}) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [verificationResults, setVerificationResults] = useState<any>(null);
  const [verificationServices] = useState<VerificationService[]>([
    {
      id: 'jumio',
      name: 'Jumio ID Verification',
      description: 'Verifica automatica di documenti di identità con AI e biometria',
      logo: '🆔',
      connected: true,
      lastCheck: '2024-01-20',
      status: 'active'
    },
    {
      id: 'onfido',
      name: 'Onfido Identity',
      description: 'Controllo identità e background check completo',
      logo: '👤',
      connected: false,
      status: 'pending'
    },
    {
      id: 'trulioo',
      name: 'Trulioo GlobalGateway',
      description: 'Verifica globale di identità e controlli AML/KYC',
      logo: '🌍',
      connected: true,
      lastCheck: '2024-01-19',
      status: 'active'
    },
    {
      id: 'shufti',
      name: 'Shufti Pro',
      description: 'Verifica OCR e riconoscimento facciale in tempo reale',
      logo: '📱',
      connected: false,
      status: 'pending'
    }
  ]);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    try {
      const { data: user } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (user) {
        setUserData(user);
        
        // Simula documenti caricati
        setDocuments([
          {
            id: '1',
            type: 'passport',
            name: 'Passaporto',
            status: 'uploaded',
            uploadDate: '2024-01-15',
            verified: false
          },
          {
            id: '2',
            type: 'driver_license',
            name: 'Patente di Guida',
            status: 'pending',
            uploadDate: null,
            verified: false
          },
          {
            id: '3',
            type: 'utility_bill',
            name: 'Bolletta',
            status: 'uploaded',
            uploadDate: '2024-01-16',
            verified: true
          }
        ]);

        // Simula risultati verifica automatica
        if (!user.identity_verified) {
          setVerificationResults({
            overall_score: 85,
            checks: {
              document_authenticity: { score: 90, status: 'pass', details: 'Documento autentico verificato' },
              face_match: { score: 88, status: 'pass', details: 'Volto corrisponde al documento' },
              address_verification: { score: 75, status: 'warning', details: 'Indirizzo parzialmente verificato' },
              background_check: { score: 92, status: 'pass', details: 'Nessun precedente negativo' },
              sanctions_check: { score: 100, status: 'pass', details: 'Non presente in liste di sanzioni' }
            },
            recommendation: 'approve',
            confidence: 'high'
          });
        }
      }
    } catch (error) {
      console.error('Errore caricamento utente:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAutomaticVerification = async (serviceId: string) => {
    try {
      // Simula chiamata API al servizio di verifica
      alert(`Avviando verifica automatica con ${serviceId}...`);
      
      // In produzione, questo farebbe una chiamata all'API del servizio
      setTimeout(() => {
        setVerificationResults((prev: Record<string, any>) => ({
          ...prev,
          [serviceId]: {
            status: 'completed',
            score: 85 + Math.random() * 15,
            timestamp: new Date().toISOString(),
            details: `Verifica completata tramite ${serviceId}`
          }
        }));
      }, 2000);
    } catch (error) {
      console.error('Errore verifica automatica:', error);
    }
  };

  const approveVerification = async () => {
    try {
      await supabase
        .from('user_profiles')
        .update({ 
          identity_verified: true,
          verification_date: new Date().toISOString(),
          verification_method: 'manual_admin'
        })
        .eq('id', userId);

      alert('Utente verificato con successo!');
      onVerificationComplete();
    } catch (error) {
      console.error('Errore approvazione verifica:', error);
      alert('Errore durante l\'approvazione della verifica');
    }
  };

  const rejectVerification = async () => {
    try {
      await supabase
        .from('user_profiles')
        .update({ 
          identity_verified: false,
          verification_notes: 'Verifica respinta dall\'amministratore'
        })
        .eq('id', userId);

      alert('Verifica respinta');
      onVerificationComplete();
    } catch (error) {
      console.error('Errore rifiuto verifica:', error);
      alert('Errore durante il rifiuto della verifica');
    }
  };

  const handleFileUpload = (documentType: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Simula upload documento
      setDocuments(prev => prev.map(doc => 
        doc.type === documentType 
          ? { ...doc, status: 'uploaded', uploadDate: new Date().toISOString(), fileName: file.name }
          : doc
      ));
      alert(`Documento ${file.name} caricato con successo!`);
    }
  };

  const getInitials = (name: string, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'passport': return '📘';
      case 'driver_license': return '🚗';
      case 'utility_bill': return '📄';
      default: return '📋';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass': return <CheckIcon size={16} style={{ color: designSystem.colors.status.success }} />;
      case 'warning': return '⚠️';
      case 'fail': return <CloseIcon size={16} style={{ color: designSystem.colors.status.error }} />;
      default: return '⏳';
    }
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['2xl'], marginBottom: designSystem.spacing.md }}>
            ⏳
          </div>
          Caricamento pannello di verifica...
        </div>
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['2xl'], marginBottom: designSystem.spacing.md }}>
            ❌
          </div>
          Utente non trovato
        </div>
      </Container>
    );
  }

  return (
    <Container
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
    >
      <Header>
        <h2>
          <VerifiedIcon size={24} />
          Pannello di Verifica Identità
        </h2>
        <CloseButton onClick={onClose}>✖️</CloseButton>
      </Header>

      <UserInfo>
        <div className="avatar">
          {getInitials(userData.full_name, userData.email)}
        </div>
        <div className="details">
          <div className="name">{userData.full_name || 'Nome non specificato'}</div>
          <div className="email">{userData.email}</div>
        </div>
        <div className="status">
          <StatusBadge status={userData.identity_verified ? 'verified' : 'pending'}>
            {userData.identity_verified ? '✅ Verificato' : '⏳ In attesa'}
          </StatusBadge>
        </div>
      </UserInfo>

      <VerificationSections>
        <VerificationSection>
          <div className="section-header">
            <h3>📋 Documenti Richiesti</h3>
            <div className="description">
              Carica e verifica i documenti di identità dell'utente
            </div>
          </div>
          <div className="section-content">
            <DocumentGrid>
              {documents.map(doc => (
                <DocumentCard 
                  key={doc.id} 
                  className={doc.status === 'uploaded' ? 'uploaded' : doc.status === 'rejected' ? 'rejected' : ''}
                >
                  <div className="document-icon">{getDocumentIcon(doc.type)}</div>
                  <div className="document-name">{doc.name}</div>
                  <div className="document-status">
                    {doc.status === 'uploaded' && (
                      <StatusBadge status={doc.verified ? 'verified' : 'pending'}>
                        {doc.verified ? '✅ Verificato' : '⏳ In verifica'}
                      </StatusBadge>
                    )}
                    {doc.status === 'pending' && (
                      <FileUpload>
                        <input 
                          type="file" 
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileUpload(doc.type, e)}
                        />
                        <UploadIcon size={16} />
                        Carica
                      </FileUpload>
                    )}
                  </div>
                  {doc.uploadDate && (
                    <div style={{ fontSize: designSystem.typography.fontSize.xs, color: designSystem.colors.text.secondary, marginTop: designSystem.spacing.xs }}>
                      {new Date(doc.uploadDate).toLocaleDateString('it-IT')}
                    </div>
                  )}
                </DocumentCard>
              ))}
            </DocumentGrid>
          </div>
        </VerificationSection>

        <VerificationSection>
          <div className="section-header">
            <h3>🔗 Servizi di Verifica Integrati</h3>
            <div className="description">
              Connetti servizi di verifica automatica per controlli AI e biometrici
            </div>
          </div>
          <div className="section-content">
            <ServiceIntegration>
              <div className="service-grid">
                {verificationServices.map(service => (
                  <ServiceCard key={service.id} connected={service.connected}>
                    <div className="service-logo">{service.logo}</div>
                    <div className="service-name">{service.name}</div>
                    <div className="service-description">{service.description}</div>
                    <div className="service-status">
                      <StatusBadge status={service.connected ? 'verified' : 'pending'}>
                        {service.connected ? '🔗 Connesso' : '⚪ Non connesso'}
                      </StatusBadge>
                    </div>
                    <div className="service-actions">
                      {service.connected ? (
                        <ActionButton 
                          variant="primary"
                          onClick={() => runAutomaticVerification(service.id)}
                        >
                          🚀 Avvia Verifica
                        </ActionButton>
                      ) : (
                        <ActionButton variant="secondary">
                          <ExternalLinkIcon size={16} />
                          Configura
                        </ActionButton>
                      )}
                    </div>
                    {service.lastCheck && (
                      <div style={{ fontSize: designSystem.typography.fontSize.xs, color: designSystem.colors.text.secondary, marginTop: designSystem.spacing.sm }}>
                        Ultimo controllo: {new Date(service.lastCheck).toLocaleDateString('it-IT')}
                      </div>
                    )}
                  </ServiceCard>
                ))}
              </div>
            </ServiceIntegration>
          </div>
        </VerificationSection>

        {verificationResults && (
          <VerificationSection>
            <div className="section-header">
              <h3>📊 Risultati Verifica Automatica</h3>
              <div className="description">
                Risultati dei controlli automatici di identità e background
              </div>
            </div>
            <div className="section-content">
              <VerificationResults>
                <div className="result-header">
                  <h4>Punteggio Complessivo: {verificationResults.overall_score}/100</h4>
                  <StatusBadge status={verificationResults.overall_score >= 80 ? 'verified' : verificationResults.overall_score >= 60 ? 'pending' : 'rejected'}>
                    {verificationResults.recommendation === 'approve' ? '✅ Approvato' : 
                     verificationResults.recommendation === 'review' ? '⏳ Da rivedere' : '❌ Respinto'}
                  </StatusBadge>
                </div>

                {Object.entries(verificationResults.checks || {}).map(([key, check]: [string, any]) => (
                  <div key={key} className="result-item">
                    <span className="label">{check.details}</span>
                    <div className="value">
                      {getStatusIcon(check.status)}
                      <span>{check.score}/100</span>
                    </div>
                  </div>
                ))}

                <div className="result-item">
                  <span className="label">Livello di Confidenza</span>
                  <div className="value">
                    <StatusBadge status={verificationResults.confidence === 'high' ? 'verified' : verificationResults.confidence === 'medium' ? 'pending' : 'rejected'}>
                      {verificationResults.confidence === 'high' ? '🟢 Alto' :
                       verificationResults.confidence === 'medium' ? '🟡 Medio' : '🔴 Basso'}
                    </StatusBadge>
                  </div>
                </div>
              </VerificationResults>
            </div>
          </VerificationSection>
        )}
      </VerificationSections>

      <ActionButtons>
        <ActionButton variant="secondary" onClick={onClose}>
          Annulla
        </ActionButton>
        
        {!userData.identity_verified && (
          <>
            <ActionButton variant="danger" onClick={rejectVerification}>
              <CloseIcon size={16} />
              Respingi Verifica
            </ActionButton>
            
            <ActionButton variant="success" onClick={approveVerification}>
              <CheckIcon size={16} />
              Approva Verifica
            </ActionButton>
          </>
        )}

        {userData.identity_verified && (
          <ActionButton variant="danger" onClick={() => {
            if (window.confirm('Sei sicuro di voler revocare la verifica per questo utente?')) {
              rejectVerification();
            }
          }}>
            🚫 Revoca Verifica
          </ActionButton>
        )}
      </ActionButtons>
    </Container>
  );
};

export default VerificationPanel; 